import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Captcha from '../../components/ui/Captcha';
import { Eye, EyeOff, Mail, Lock, AlertCircle, Copy } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [captchaReset, setCaptchaReset] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const from = location?.state?.from?.pathname || '/developer-portal';

  const demoCredentials = [
    { email: 'shad@gmail.com', password: '123123', role: 'Admin' },
  ];
  //diyAnA@4879$

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    if (!captchaToken) {
      setCaptchaError('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);
    setError('');
    setCaptchaError('');

    try {
      const { error } = await signIn(email, password, captchaToken);
      
      if (error) {
        setError(error?.message);
      } else {
        navigate(from, { replace: true });
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fillCredentials = (credentials) => {
    setEmail(credentials?.email);
    setPassword(credentials?.password);
    setError('');
    setCaptchaError('');
    setCaptchaToken(null);
    setCaptchaReset(prev => !prev); // Trigger reset
  };

  const handleCaptchaVerify = (token, error) => {
    if (error) {
      setCaptchaError(error);
      setCaptchaToken(null);
    } else {
      setCaptchaToken(token);
      setCaptchaError('');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard?.writeText(text);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body?.appendChild(textArea);
      textArea?.focus();
      textArea?.select();
      document.execCommand('copy');
      document.body?.removeChild(textArea);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 relative overflow-hidden">
      {/* Background Pattern */}
      <div 
        className="absolute inset-0 opacity-50" 
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.03'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3Ccircle cx='53' cy='7' r='1'/%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3Ccircle cx='7' cy='53' r='1'/%3E%3Ccircle cx='53' cy='53' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      ></div>
      
      {/* Floating Orbs */}
      <div className="absolute top-20 left-20 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      
      <Header className="relative z-10" />
      
      <main className="relative z-10 pt-24 pb-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-12rem)]">
            
            {/* Left Side - Centered Text */}
            <div className="flex flex-col items-center justify-center text-center animate-fade-in">
              <h1 className="text-5xl font-bold text-foreground mb-6 bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
                Welcome back
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed max-w-md">
                Sign in to your MessagingHub Pro account and continue your journey
              </p>
            </div>

            {/* Right Side - Login Form */}
            <div className="flex items-center justify-center">
              <div className="w-full max-w-md animate-slide-up">
                <div className="bg-card/90 border border-border/50 rounded-2xl p-8 relative overflow-hidden">
                  
                  <form className="space-y-6 relative" onSubmit={handleSubmit}>
                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                        Email address
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e?.target?.value)}
                          className="block w-full pl-12 pr-4 py-4 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-200 hover:border-primary/30"
                          placeholder="Enter your email"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="password" className="block text-sm font-semibold text-foreground mb-2">
                        Password
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          required
                          value={password}
                          onChange={(e) => setPassword(e?.target?.value)}
                          className="block w-full pl-12 pr-14 py-4 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-200 hover:border-primary/30"
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-muted-foreground hover:text-primary transition-all duration-200 hover:scale-110"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* CAPTCHA */}
                    <div className="space-y-2">
                      <Captcha
                        onVerify={handleCaptchaVerify}
                        error={captchaError}
                        resetTrigger={captchaReset}
                      />
                    </div>

                    {error && (
                      <div className="flex items-center space-x-3 text-error bg-error/10 border border-error/20 p-4 rounded-xl animate-shake">
                        <AlertCircle className="h-5 w-5 flex-shrink-0" />
                        <span className="text-sm font-medium">{error}</span>
                      </div>
                    )}

                    <div className="pt-2">
                      <Button
                        type="submit"
                        disabled={loading}
                        loading={loading}
                        fullWidth
                        size="lg"
                        className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary border-0 transform hover:scale-[1.02] transition-all duration-200"
                      >
                        {loading ? 'Signing in...' : 'Sign in'}
                      </Button>
                    </div>
                  </form>

                  {/* Demo Credentials Section */}
                  <div className="mt-8 pt-6 border-t border-border/30">
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border/30" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-card/80 backdrop-blur-xl text-muted-foreground font-medium">Demo Credentials</span>
                      </div>
                    </div>

                    <div className="mt-6">
                      <div className="bg-gradient-to-br from-primary/5 to-secondary/5 border border-primary/20 rounded-xl p-5 backdrop-blur-sm">
                        <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center">
                          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                            <Icon name="User" size={16} className="text-primary" />
                          </div>
                          Test Account Access
                        </h4>
                        <div className="space-y-3">
                          {demoCredentials?.map((cred, index) => (
                            <div key={index} className="flex items-center justify-between text-sm p-3 bg-background/50 rounded-lg border border-border/30 hover:border-primary/30 transition-all duration-200">
                              <div className="flex-1">
                                <span className="font-semibold text-foreground">{cred?.role}:</span>
                                <span className="ml-2 text-muted-foreground font-mono text-xs">{cred?.email}</span>
                                <span className="mx-2 text-muted-foreground">•</span>
                                <span className="text-muted-foreground font-mono text-xs">{cred?.password}</span>
                              </div>
                              <div className="flex space-x-2 ml-4">
                                <Button
                                  onClick={() => fillCredentials(cred)}
                                  size="xs"
                                  variant="outline"
                                  className="hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-200"
                                >
                                  Use
                                </Button>
                                <Button
                                  onClick={() => copyToClipboard(`${cred?.email}:${cred?.password}`)}
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 hover:bg-primary/10 hover:text-primary transition-all duration-200"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Forgot Password Link */}
                  <div className="mt-6 text-center">
                    <Link 
                      to="/auth/forgot-password" 
                      className="text-sm text-primary hover:text-primary/80 font-medium transition-all duration-200 hover:underline underline-offset-4 relative z-10"
                    >
                      Forgot your password?
                    </Link>
                  </div>

                  {/* Signup Link */}
                  <div className="mt-4 text-center">
                    <span className="text-sm text-muted-foreground">
                      Don't have an account?{' '}
                    </span>
                    <Link 
                      to="/auth/signup" 
                      className="text-primary hover:text-primary/80 font-medium transition-all duration-200 hover:underline underline-offset-4 relative z-10 ml-1"
                    >
                      Create one here
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;